/**
 * A gradient text and a moving descendant cannot share one element — and every text animation is a
 * moving descendant.
 *
 * WHY THIS EXISTS (founder, 2026-09-22: "the animation doesn't work on text when we add gradient color
 * to it"). A gradient on text is `background-image` + `background-clip:text` on the heading (block
 * gradient, section "Couleurs du texte", bold/italic highlights — all of them). The split and line
 * animations then wrap the words / lines in spans that get a `transform` and `will-change`. Chromium
 * promotes each such span to its own compositor layer, and a parent's `background-clip:text` cannot
 * paint through a composited child: the child's glyphs are the clip, the parent's gradient is the ink,
 * and they now live on two different layers. Result, seen in a real Chromium: NOTHING is painted —
 * not before, not during and (for the split words, whose spans stay) not after the animation either.
 * Typing (`visibility`) and the whole-element fade/blur are untouched: nothing composited sits between
 * the ink and the glyphs.
 *
 * THE FIX: give every mover its own copy of the gradient, sized to the clipped ancestor's box and
 * offset by where the mover sits in it — so the ink is painted on the layer that also holds the glyphs,
 * and the seams line up: a word at x=120px shows the same slice of the gradient it showed as part of
 * the whole. The ancestor keeps its own paint (its own text nodes, if any, still need it). Once the
 * animation lands, both engines put the original markup back, so a later reflow meets one gradient on
 * one element, never stale offsets.
 */
/** The nearest ancestor of `from` (inclusive) that paints a gradient clipped to its text, or `null`. */
function clipSource(from, view) {
    let el = from;
    while (el && el !== el.ownerDocument.body) {
        const cs = view.getComputedStyle(el);
        const clip = cs.backgroundClip === 'text' || cs.webkitBackgroundClip === 'text';
        if (clip && cs.backgroundImage && cs.backgroundImage !== 'none')
            return el;
        el = el.parentElement;
    }
    return null;
}
/** The translation an element currently carries (its own `transform` only), so a measure can undo it. */
function ownOffset(cs) {
    const t = cs.transform;
    if (!t || t === 'none')
        return { dx: 0, dy: 0 };
    const m2 = /^matrix\(([^)]+)\)$/.exec(t);
    if (m2) {
        const p = m2[1].split(',').map(Number);
        return { dx: p[4] ?? 0, dy: p[5] ?? 0 };
    }
    const m3 = /^matrix3d\(([^)]+)\)$/.exec(t);
    if (m3) {
        const p = m3[1].split(',').map(Number);
        return { dx: p[12] ?? 0, dy: p[13] ?? 0 };
    }
    return { dx: 0, dy: 0 };
}
function inkOf(el, view) {
    const cs = view.getComputedStyle(el);
    const r = el.getBoundingClientRect();
    // The background box is the PADDING box: inside the border, outside the padding. An inline element
    // (a highlighted `<strong>`) has no client box; its fragments' union is the best it offers.
    const w = el.clientWidth || r.width;
    const h = el.clientHeight || r.height;
    return { image: cs.backgroundImage, x: r.left + el.clientLeft, y: r.top + el.clientTop, w, h };
}
/**
 * Paint the gradient of the nearest text-clipped ancestor onto each of `units`, aligned so the whole
 * still reads as ONE gradient. Units with no such ancestor are left alone; the function is a no-op then,
 * and costs one style read per ancestor step. Call it AFTER the units are laid out where they will
 * animate (display set, in the document) and BEFORE their start transform, or at least while their
 * transform is a plain translation — the measure undoes that.
 */
export function paintClippedGradient(units) {
    if (units.length === 0)
        return;
    const view = units[0].ownerDocument.defaultView;
    if (!view)
        return;
    // Reads first (one layout), writes after — never interleaved.
    const sources = new Map();
    const inks = new Map();
    const plan = [];
    for (const unit of units) {
        const parent = unit.parentElement;
        if (!parent)
            continue;
        let src = sources.get(parent);
        if (src === undefined) {
            src = clipSource(parent, view);
            sources.set(parent, src);
        }
        if (!src)
            continue;
        let ink = inks.get(src);
        if (!ink) {
            ink = inkOf(src, view);
            inks.set(src, ink);
        }
        const r = unit.getBoundingClientRect();
        const { dx, dy } = ownOffset(view.getComputedStyle(unit));
        plan.push({ unit, ink, left: r.left - dx, top: r.top - dy });
    }
    for (const { unit, ink, left, top } of plan) {
        const s = unit.style;
        s.backgroundImage = ink.image;
        s.backgroundSize = `${ink.w}px ${ink.h}px`;
        s.backgroundPosition = `${ink.x - left}px ${ink.y - top}px`;
        s.setProperty('-webkit-background-clip', 'text');
        s.backgroundClip = 'text';
        s.setProperty('-webkit-text-fill-color', 'transparent');
    }
}
//# sourceMappingURL=clippedTextGradient.js.map